import type { DeepPartial } from '@/features/shared/types'
import { PropertyStatus, type Status } from './PropertyStatus'

export function cleanNotPresent<TModel extends object>(
  model: TModel,
  status: Status<TModel> = {},
  notPresentValue = null,
): [TModel, DeepPartial<TModel>] {
  return Object.entries(model).reduce(
    ([newModel, notPresentModel], [key, value]) => {
      // Array
      if (value && value instanceof Array) {
        const deepStatus = (status as any)[key]
        if (!deepStatus) {
          ; (newModel as any)[key] = value
          return [newModel, notPresentModel]
        }

        if (deepStatus.$status === PropertyStatus.NotPresent) {
          (newModel as any)[key] = notPresentValue;
          (notPresentModel as any)[key] = value;
          return [newModel, notPresentModel]
        }

        // Si le tableau contient des valeurs
        if (typeof value?.at(0) !== 'object') {
          if (deepStatus.$status === PropertyStatus.NotPresent) {
            ; (newModel as any)[key] = notPresentValue
              ; (notPresentModel as any)[key] = value
            return [newModel, notPresentModel]
          } else {
            ; (newModel as any)[key] = value
            return [newModel, notPresentModel]
          }
        }

        // Si le tableau contient des objets
        const deepValue = []
        const deepNotPresentValue = []
        for (let index = 0; index < value.length; index++) {
          const itemStatus = (deepStatus as any)[index]
          const itemValue = value[index]

          // Si l'item est notPresent, l'éliminer des tableaux
          if (itemStatus?.$status === PropertyStatus.NotPresent) {
            continue
          }

          if (!itemStatus || !itemValue) {
            deepValue.push(itemValue)
            deepNotPresentValue.push(undefined)
            continue
          }

          const [deepItemValue, deepItemNotPresentValue] = cleanNotPresent(itemValue, itemStatus, notPresentValue)
          deepValue.push(deepItemValue)
          deepNotPresentValue.push(deepItemNotPresentValue)
        }
        ; (newModel as any)[key] = deepValue
          ; (notPresentModel as any)[key] = deepNotPresentValue
        return [newModel, notPresentModel]
      }

      // Object
      if (value && typeof value === 'object') {
        const deepStatus = (status as any)[key]
        if (!deepStatus) {
          (newModel as any)[key] = value;
          return [newModel, notPresentModel]
        }

        if (deepStatus.$status === PropertyStatus.NotPresent) {
          (newModel as any)[key] = notPresentValue;
          (notPresentModel as any)[key] = value;
          return [newModel, notPresentModel]
        }

        const [deepValue, deepNotPresentValue] = cleanNotPresent(value, deepStatus, notPresentValue);
        (newModel as any)[key] = deepValue;
        (notPresentModel as any)[key] = deepNotPresentValue;
        return [newModel, notPresentModel]
      }

      // Members
      if ((status as any)[key]?.$status === PropertyStatus.NotPresent) {
        ; (newModel as any)[key] = notPresentValue
          ; (notPresentModel as any)[key] = value
      } else {
        ; (newModel as any)[key] = value
      }
      return [newModel, notPresentModel]
    },
    [{} as TModel, {} as DeepPartial<TModel>],
  )
}

export type RestorePriority = 'modelFirst' | 'notPresentFirst'
export function restoreNotPresent<TModel extends object>(
  model: TModel,
  notPresentModel: DeepPartial<TModel>,
  priority: RestorePriority = 'notPresentFirst',
) {
  const allKeys = new Set([...Object.keys(model ?? {}), ...Object.keys(notPresentModel ?? {})])
  return [...allKeys].reduce((newObject, key) => {
    const value =
      priority === 'notPresentFirst'
        ? (((notPresentModel as any) ?? {})[key] ?? ((model as any) ?? {})[key])
        : (((model as any) ?? {})[key] ?? ((notPresentModel as any) ?? {})[key])
    if (Array.isArray(value)) {
      if (value.length && typeof value.at(0) === 'object') {
        newObject[key] = value.map((_, index) => {
          const notPresentObjet = (notPresentModel as any)[key]?.at(index)
          if (!notPresentObjet) {
            return (model as any)[key]?.at(index)
          } else {
            return restoreNotPresent((model as any)[key]?.at(index) ?? {}, notPresentObjet, priority)
          }
        })
      } else {
        newObject[key] = value
      }
    } else if (value && typeof value === 'object') {
      const notPresentObjet = (notPresentModel as any)[key]
      if (!notPresentObjet) {
        newObject[key] = (model as any)[key]
      } else {
        newObject[key] = restoreNotPresent((model as any)[key] ?? {}, notPresentObjet, priority)
      }
    } else {
      newObject[key] = value
    }

    return newObject
  }, {} as any) as TModel
}

